import { all, put, call, select, getContext, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { Logger } from 'utils/Logger';
import { Analytics } from 'utils/Analytics';
import { isEmpty } from 'utils/ObjectHelper';

import { AnalyticsEventCategory, AnalyticsEventAction } from 'enums/analytics-types';

import { makeSelectClientIdBySearchProfileId } from 'reducers/fundingSearch/selectors';
import {
  removeDocument,
  uploadDocuments,
  fundingSearchProfile,
  updateProfileSection,
  markProfileSectionAsComplete,
  needUpdateProfileSection,
  REQUEST_AUTO_SAVE_PROFILE_SECTION,
  serviceabilityScore,
  serviceabilityPriceEstimates,
  SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS,
  SSE_UPDATE_SNAPSHOT_PROFILE_PROGRESS,
} from './types';

import {
  requestUploadDocumentsSuccess,
  requestRemoveDocumentSuccess,
  requestFundingSearchProfileSuccess,
  requestFundingSearchProfileFailure,
  requestUpdateProfileSectionSuccess,
  requestUpdateProfileSectionFailure,
  requestNeedUpdateProfileSectionSuccess,
  requestNeedUpdateProfileSectionFailure,
  requestServiceabilityScoreSuccess,
  requestServiceabilityScoreFailure,
  requestServiceabilityPriceEstimatesSuccess,
  requestServiceabilityPriceEstimatesFailure,
  setSnapshotCreating,
  requestFundingSearchProfile,
} from './reducer';

import {
  requestUploadFundingDocumentSaga,
  requestRemoveFundingDocumentSaga,
} from '../documents/sagas';

import * as fundingSearchActions from '../fundingSearch/actions';
import { makeSelectMasquerade } from '../auth/selectors';
import { makeSelectProfileFundingSearchId } from './selectors';
import { requestClientTimeline } from 'reducers/monitor/timeline/actions';

const logger = new Logger('Saga:FundingSearchProfile');

export function* requestFundingSearchProfileSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.funding_search_profile', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    yield put(requestFundingSearchProfileSuccess(action.fundingSearchId, response.data));
  } catch (error) {
    logger.error('Failed to request funding search profile with the following error', error);
    yield put(requestFundingSearchProfileFailure(action.fundingSearchId, error));
  }
}

export function* requestServiceabilityPriceEstimates(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.platform.funding_search_price_estimates', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    yield put(requestServiceabilityPriceEstimatesSuccess(action.sectionId, response.data));
  } catch (error) {
    logger.error('Failed to request funding search profile with the following error', error);
    yield put(requestServiceabilityPriceEstimatesFailure(action.fundingSearchId, error));
  }
}

export function* requestUpdateProfileSectionSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  const cleanPayload = Array.isArray(action.payload)
    ? action.payload.filter((value) => !isEmpty(value))
    : action.payload;

  try {
    const response = yield call(
      authApiService.put,
      routerService.generate('api.update_funding_search_profile_section', {
        fundingSearchId: action.fundingSearchId,
        sectionId: action.sectionId,
      }),
      cleanPayload,
    );

    const profileCompletion = response.data.completion;

    yield put(
      requestUpdateProfileSectionSuccess(action.fundingSearchId, action.sectionId, response.data),
    );
    yield put(
      fundingSearchActions.setFundingSearchProfileCompletion(
        action.fundingSearchId,
        profileCompletion,
      ),
    );
    action.promise && action.promise.resolve({ profileCompletion });
  } catch (error) {
    logger.error('Failed to update funding search profile section with the following error', error);
    yield put(requestUpdateProfileSectionFailure(action.fundingSearchId, action.sectionId, error));
    action.promise && action.promise.reject();
  }
}

export function* requestMarkProfileSectionAsCompleteSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  const cleanPayload = Array.isArray(action.payload)
    ? action.payload.filter((value) => !isEmpty(value))
    : action.payload;

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.complete_funding_search_profile_section', {
        fundingSearchId: action.fundingSearchId,
        sectionId: action.sectionId,
      }),
      cleanPayload,
    );

    yield put(
      requestUpdateProfileSectionSuccess(action.fundingSearchId, action.sectionId, response.data),
    );
    yield put(
      fundingSearchActions.setFundingSearchProfileCompletion(
        action.fundingSearchId,
        response.data.completion,
      ),
    );
    action.promise && action.promise.resolve();
  } catch (error) {
    logger.error(
      'Failed to mark funding search profile section as marked with the following error',
      error,
    );
    yield put(requestUpdateProfileSectionFailure(action.fundingSearchId, action.sectionId, error));
    action.promise && action.promise.reject();
  }
}

export function* requestAutoSaveProfileSectionSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    // FIXME: Change this when endpoint is available
    yield call(
      authApiService.put,
      routerService.generate('api.update_funding_search_profile_section', {
        fundingSearchId: action.fundingSearchId,
        sectionId: action.sectionId,
      }),
      action.payload,
    );

    action.promise && action.promise.resolve();
  } catch (error) {
    logger.error(
      'Failed to auto save funding search profile section with the following error',
      error,
    );
    action.promise && action.promise.reject();
  }
}

export function* requestUploadDocumentsSaga(action) {
  try {
    const response = yield call(
      requestUploadFundingDocumentSaga,
      action.identifier,
      action.fundingSearchId,
      action.documentType,
      action.files,
    );

    // Trigger the success action to add the documents on the correct section
    yield put(
      requestUploadDocumentsSuccess(
        action.fundingSearchId,
        action.documentType,
        response.data.documents,
      ),
    );

    const isMasquerading = yield select(makeSelectMasquerade());

    Analytics.track(
      AnalyticsEventCategory.fundingSearchProfile,
      AnalyticsEventAction.profileDocumentUpload,
      {
        extraParams: { isMasquerading: !!isMasquerading },
        joinCategory: false,
      },
    );
  } catch (error) {
    logger.error('Failed to upload funding search documents with the following error', error);
  }
}

export function* requestRemoveDocumentSaga(action) {
  try {
    yield call(requestRemoveFundingDocumentSaga, action.fundingSearchId, action.documentId);

    // Trigger the success action to add the documents on the correct section
    yield put(
      requestRemoveDocumentSuccess(action.fundingSearchId, action.documentId, action.documentType),
    );

    const isMasquerading = yield select(makeSelectMasquerade());

    Analytics.track(
      AnalyticsEventCategory.fundingSearchProfile,
      AnalyticsEventAction.profileDocumentRemove,
      {
        extraParams: { isMasquerading: !!isMasquerading },
        joinCategory: false,
      },
    );
  } catch (error) {
    logger.error('Failed to remove funding search documents with the following error', error);
  }
}

export function* requestNeedUpdateProfileSectionSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield call(
      authApiService.post,
      routerService.generate('api.request_update_funding_search_profile_section', {
        fundingSearchId: action.fundingSearchId,
        sectionId: action.sectionId,
      }),
    );

    yield put(requestNeedUpdateProfileSectionSuccess(action.fundingSearchId, action.sectionId));
    action.promise && action.promise.resolve();
  } catch (error) {
    logger.error(
      'Failed to request needed update on funding search profile section with the following error',
      error,
    );
    yield put(
      requestNeedUpdateProfileSectionFailure(action.fundingSearchId, action.sectionId, error),
    );
    action.promise && action.promise.reject();
  }
}

export function* requestServiceabilityScoreSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.platform.serviceability_score'),
      action.payload,
    );

    yield put(requestServiceabilityScoreSuccess(action.sectionId, response.data));
  } catch (error) {
    logger.error('Failed to calculate serviceability score with the following error', error);
    yield put(requestServiceabilityScoreFailure(action.sectionId, error));
  }
}

export function* requestSnapshotUpdateSaga(action) {
  yield call(requestProfileUpdateSaga, {
    type: action?.type,
    payload: {
      ...action?.payload,
      isSnapshotGeneration: true,
    },
  });
}

export function* requestProfileUpdateSaga(action) {
  const entityId = action?.payload?.entityId;
  const error = action?.payload?.error;
  const clientId = yield select(makeSelectClientIdBySearchProfileId());
  const isSnapshotGeneration = action?.payload?.isSnapshotGeneration;
  const finished = action?.payload?.finished && !isSnapshotGeneration;

  if (parseInt(clientId, 10) !== parseInt(entityId, 10)) {
    return;
  }

  if (error) {
    yield put(setSnapshotCreating(false));
    logger.error('Failed to generate snapshot', error);

    return;
  }

  yield put(setSnapshotCreating(!finished));

  if (!finished) {
    return;
  }

  const fundingSearchId = yield select(makeSelectProfileFundingSearchId());
  yield put(fundingSearchActions.requestFundingSearch(fundingSearchId, { includeRules: true }));
  yield put(requestFundingSearchProfile(fundingSearchId));
}

export default function* fundingSearchSaga() {
  return yield all([
    takeLatest(fundingSearchProfile.request, requestFundingSearchProfileSaga),
    takeLatest(updateProfileSection.request, requestUpdateProfileSectionSaga),
    takeLatest(markProfileSectionAsComplete.request, requestMarkProfileSectionAsCompleteSaga),
    takeLatest(REQUEST_AUTO_SAVE_PROFILE_SECTION, requestAutoSaveProfileSectionSaga),
    takeLatest(uploadDocuments.request, requestUploadDocumentsSaga),
    takeLatest(removeDocument.request, requestRemoveDocumentSaga),
    takeLatest(needUpdateProfileSection.request, requestNeedUpdateProfileSectionSaga),
    takeLatest(serviceabilityScore.request, requestServiceabilityScoreSaga),
    takeLatest(serviceabilityPriceEstimates.request, requestServiceabilityPriceEstimates),
    takeLatest(SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS, requestSnapshotUpdateSaga),
    takeLatest(SSE_UPDATE_SNAPSHOT_PROFILE_PROGRESS, requestProfileUpdateSaga),
  ]);
}
